import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spin, Form, Result, Breadcrumb, Modal, message, Tooltip, Button } from "antd";
import get from "lodash/get";
import map from "lodash/map";
import find from "lodash/find";

import dayjs from "dayjs";
import { useMediaQuery } from "react-responsive";
import { CalendarOutlined, DownloadOutlined, SettingOutlined } from "@ant-design/icons";

import requireAuth from "../../hocs/requireAuth";
import {
  deleteCompanyTemplateShiftConfig,
  fetchCompanyTemplatesShiftConfig,
} from "../../redux/actions/templateActions";
import {
  fetchCompanies,
  fetchCompanyStaffs,
} from "../../redux/actions/companyActions";
import HeaderSelector from "../../components/shiftSetting/headerSelector";
import ShiftSettingForm from "../../components/shiftSetting/shiftSettingForm";
import TemplateListOverview from "../../components/shiftSetting/expendedRowRender";
import { SHIFT_SETTING_CONFIG_DELETE_SUCCESS } from "../../constants/message";
import { useLocation } from "react-router-dom";
import { getQueryParams } from "../../utilities/getAndOnParams";
import { fetchJobCard } from "../../redux/actions/downloadPdfAction";
import { fetchConfig } from "../../redux/actions/authActions";
import PunchPressShiftSettingForm from "../../components/shiftSetting/punchPressShiftSettingForm";

const initialState = {
  company: undefined,
  templateType: undefined,
  date: dayjs(),
};

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <SettingOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <CalendarOutlined style={{ fontSize: "16px" }} />
              <span>Shift</span>
            </>
          ),
        },
        {
          title: "Settings",
        },
      ]}
    />
  );
};

const ShiftSettingScreen = () => {
  const isStaff = useSelector((state) => get(state, "auth.isStaff"))
  const location = useLocation();
  const { company_uuid, template_type } = getQueryParams(location.search);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [companyStaffs, setCompanyStaffs] = useState({
    operators: [],
    superVisors: [],
    managers: []
  });

  const [shiftConfigDrawer, setShiftConfigDrawer] = useState({
    visible: false,
    data: null,
  });

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const toggleShiftConfigDrawer = (data = null) => {
    setShiftConfigDrawer({
      visible: !get(shiftConfigDrawer, "visible"),
      data,
    });
  };

  const [form] = Form.useForm();
  const [showEmpty, setShowEmpty] = useState(false);
  const [filter, setFilters] = useState(initialState);
  const [templates, setTemplates] = useState([]);
  const dispatch = useDispatch();

  const setinitialCompany = (company, res) => {
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });
    setConfig({
      ...res,
      companies: [company],
      template_types: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      Company: get(company, "id"),
      template_type: undefined,
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        dispatch(fetchConfig("extrusion_scrap_type,extrusion_process"))
          .then((res) => {
            if (get(result, "length") === 1) {
              setinitialCompany(result[0], res);
            } else {
              setConfig({ ...res, companies: result });
            }
          })
          .catch(() => { });

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getOpertators = (company_uuid) => {
    dispatch(fetchCompanyStaffs(company_uuid, { role_id: 3 }))
      .then((result) => {
        setCompanyStaffs((prev) => ({ ...prev, operators: result }));
      })
      .catch(() => { });
  };

  const getSupervisors = (company_uuid) => {
    dispatch(fetchCompanyStaffs(company_uuid, { role_id: 2 }))
      .then((result) => {
        setCompanyStaffs((prev) => ({ ...prev, superVisors: result }));
      })
      .catch(() => { });
  };
  const getManagers = (company_uuid) => {
    dispatch(fetchCompanyStaffs(company_uuid, { role_id: 1 }))
      .then((result) => {
        setCompanyStaffs((prev) => ({ ...prev, managers: result }));
      })
      .catch(() => { });
  };
  const getCompayTemplateShiftConfig = (query) => {
    setLoading(true);
    query = query
      ? query
      : {
        company_uuid: get(filter, "company.id"),
        type: get(filter, "templateType"),
        date: get(filter, "date").format("YYYY-MM-DD"),
      };
    dispatch(fetchCompanyTemplatesShiftConfig(query))
      .then((result) => {
        if (get(result, "length") > 0) {
          setTemplates(result);
          setShowEmpty(false);
          getOpertators(get(query, "company_uuid"));
          getSupervisors(get(query, "company_uuid"));
          getManagers(get(query, "company_uuid"));
        } else {
          setTemplates([]);
          setShowEmpty(true);
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const delCompanyTemplateShiftConfig = (config_id) => {
    setLoading(true);
    dispatch(deleteCompanyTemplateShiftConfig(config_id))
      .then((result) => {
        message.success(SHIFT_SETTING_CONFIG_DELETE_SUCCESS);
        getCompayTemplateShiftConfig();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const paramsFormHandler = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((companies) => {
        const company = find(companies, ["id", company_uuid]);
        if (!company) {
          message.error("Company not found from URL param.");
          setLoading(false);
          return;
        }

        form.setFieldsValue({
          Company: company_uuid,
          template_type: parseInt(template_type),
          date: dayjs(),
        });
        setConfig((prev) => ({
          ...prev,
          companies,
          template_types: get(company, "template_access_list"),
        }));

        setFilters((prev) => ({
          ...prev,
          company,
          templateType: Number(template_type),
          date: dayjs(),
        }));
        getSupervisors(company_uuid);
        getOpertators(company_uuid);
        getManagers(company_uuid)
        getCompayTemplateShiftConfig({
          company_uuid: company_uuid,
          type: template_type,
          date: dayjs().format("YYYY-MM-DD"),
        });
      })
      .catch(() => {
        message.error(FAILED_FETCH_COMPANINES);
        setLoading(false);
      });
  };

  const onDownloadReport = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      date: get(filter, "date").format("YYYY-MM-DD"),
    };
    dispatch(fetchJobCard(
      get(filter, "company.id")
      , query))
      .then((result) => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();

    if (company_uuid && template_type) {
      paramsFormHandler();
    }
  }, []);
  return (
    <div className={`${isMobile ? "" : "p-2"} h-full flex flex-col`}>
      <div className="flex flex-row justify-between pb-4">
        <div>
          <PageBreadcrumb />
        </div>
        <div></div>
      </div>

      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full ">
        <Spin spinning={loading}>
          <Form form={form}>
            <HeaderSelector
              form={form}
              filter={filter}
              config={config}
              setConfig={setConfig}
              setFilters={setFilters}
              setTemplates={setTemplates}
              getDetails={getCompayTemplateShiftConfig}
            />
          </Form>
          {get(templates, "length") > 0 && (
            <div
              className={`${isMobile ? "px-1" : "px-8 py-0"
                } overflow-y-auto w-full`}
            >
              <div className="float-right mb-5 space-x-2 ml-auto">
                <Tooltip className="mb-5" title="Download Job Card">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => onDownloadReport()}
                  >Download Job Card</Button>
                </Tooltip>
              </div>
              <div className="w-full overflow-y-auto">
                <TemplateListOverview
                  config={config}
                  dataSource={map(templates, (template) => ({
                    ...template,
                    key: get(template, "id"),
                    shifts: map(get(template, "shifts"), (shift) => ({
                      ...shift,
                      default_values: {
                        ...get(template, "default_values"),
                        planned_down_time: 0,
                      },
                      manager:get(template, "manager")
                    })),
                  }))}
                  toggle={toggleShiftConfigDrawer}
                  deleteConfig={delCompanyTemplateShiftConfig}
                  filter={filter}
                  isStaff={isStaff}
                />
              </div>
            </div>
          )}
          {showEmpty && (
            <div className="px-8 py-8">
              <Result
                title={`No template exists...!`}
                subTitle="Please try again with another company or template type."
                status="404"
              />
            </div>
          )}
          <Modal
            destroyOnHidden
            title={`${get(shiftConfigDrawer, "data.template_name")} - [${get(
              shiftConfigDrawer,
              "data.name"
            )}] - ${get(filter, "date").format("DD/MM/YYYY")}`}
            width="90vw"
            height="90vh"
            open={get(shiftConfigDrawer, "visible")}
            onCancel={() => {
              toggleShiftConfigDrawer();
            }}
            centered
            footer={null}
            afterClose={() => getCompayTemplateShiftConfig()}
          >
            <PunchPressShiftSettingForm
              config={config}
              operators={get(companyStaffs, "operators")}
              superVisors={get(companyStaffs, "superVisors")}
              managers={get(companyStaffs, "managers")}
              refreshManagers={() => getManagers(get(filter, "company.id"))}
              refreshOperators={() => { getOpertators(get(filter, "company.id")) }}
              refreshSuperVisors={() => { getSupervisors(get(filter, "company.id")) }}
              initialValues={get(shiftConfigDrawer, "data")}
              toggle={() => {
                toggleShiftConfigDrawer();
              }}
              filter={filter}
              onUpdateConfig={() => {
                toggleShiftConfigDrawer();
              }}
            />
          </Modal>
        </Spin>
      </div>
    </div>
  );
};

export default requireAuth(ShiftSettingScreen);